// CommandPalette.tsx — Cmd+K palette v2 (T3.1) + global search (T3.2)
//
// Group order: Actions › Recents › Navigation › [server search groups] › Agentic › Context
//
// Extending T2: nav entries from hubDefinitions + aliases are preserved.
// T3.1 added: quick-create actions (gated by persona), recent entity-detail
// pages, agentic shortcuts, and context actions.
// T3.2 (this change): the old client-side "Workflows" fuzzy filter
// (useWorkflows({limit:50}) + .includes()) is replaced by the real
// GET /tenant/search/global backend via useGlobalSearch — see that hook for
// why it's a hand-rolled debounce/abort/sequence-guard rather than
// TanStack Query. The sync-above-async group ordering is unchanged; server
// groups render in the slot the old Workflows group used to occupy.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  BookOpen,
  Bot,
  Building2,
  Calendar,
  Clock,
  GitFork,
  History,
  LayoutTemplate,
  Link2,
  Loader2,
  MessageSquare,
  Network,
  PlayCircle,
  PlusCircle,
  BarChart2,
  Search,
  SunMoon,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { useCommandPaletteStore } from './useCommandPalette'
import { navigation, hubDefinitions } from '@/components/layout/navigation'
import { usePersona, type Persona } from '@/hooks/usePersona'
import { useAuthStore } from '@/stores/authStore'
import { useTheme } from '@/hooks/useTheme'
import { useRecentItems } from './useRecentItems'
import { ACTION_DEFS, HUB_SEARCH_ALIASES, EXTRA_NAV_COMMANDS } from './actionDefinitions'
import { useGlobalSearch } from './useGlobalSearch'
import { HighlightMatch } from './HighlightMatch'
import { looksIdLike } from './idLikeQuery'
import type { GlobalSearchResultType } from './globalSearchApi'

// ── Icons for action commands (keyed by id) ───────────────────────────────────
//
// Icons live here rather than in actionDefinitions.ts to avoid serialising
// ReactNode across module boundaries and to keep actionDefinitions.ts pure
// (importable without React).

const ACTION_ICONS: Record<string, React.ReactNode> = {
  'new-workflow': <PlusCircle className="h-4 w-4 shrink-0" />,
  'new-agent-graph': <Bot className="h-4 w-4 shrink-0" />,
  'new-runbook': <BookOpen className="h-4 w-4 shrink-0" />,
  'new-team': <Users className="h-4 w-4 shrink-0" />,
  'new-schedule': <Calendar className="h-4 w-4 shrink-0" />,
  'generate-report': <BarChart2 className="h-4 w-4 shrink-0" />,
  'invite-user': <Users className="h-4 w-4 shrink-0" />,
}

// ── Navigation commands (generated from hubDefinitions + nav items) ───────────

type NavCommand = {
  id: string
  label: string
  /**
   * All terms that match this entry (label + aliases), joined with spaces.
   * The palette runs with shouldFilter={false} (see includesQuery below), so
   * this string is matched by our own case-insensitive substring filter, not
   * cmdk's built-in fuzzy search — but the intent is the same: typing an old
   * name ("executions", "airflow", "audit log") still finds the new home.
   */
  value: string
  path: string
}

/**
 * Generates navigation commands from the hub definitions and top-level nav items,
 * gated by the SAME persona predicates the sidebar/hub tab bar use.
 *
 * P2.439 (security review of P2.432, pre-existing): this used to emit a
 * command for every hub tab in `hubDefinitions` unconditionally, so a
 * role-gated tab (Audit Log, Compliance, AI Governance, Approval Gates, DSR,
 * etc. — whose `visible` checks canManageUsers/canViewAudit/canEditSettings/
 * role) was searchable and navigable from the palette by ANY tenant persona,
 * even though the same tab is hidden from the hub's own tab bar
 * (`HubTabs.tsx`'s `tabs.filter((t) => t.visible(persona))`). Server-side
 * authz still applies, but the platform's disable-up-front rule (never rely
 * on a 403 after the fact) means the palette must apply the identical gate.
 * Hub tabs carry their `aliases` field; hub items get extras from HUB_SEARCH_ALIASES
 * so that old names (e.g. "executions") still resolve to the correct hub.
 */
function buildNavCommands(persona: Persona): NavCommand[] {
  const cmds: NavCommand[] = []

  // Top-level hub items (hub entry-points). Every tenant-side entry in
  // `navigation` is a hub entry-point — including footer-pinned ones like
  // Deleted — so the only real selector is the tenant path prefix. This
  // used to also require `!item.section`, back when sectionless was
  // synonymous with "hub item"; the Workspace/Manage sidebar grouping
  // (014210b81) gave every tenant hub a `section`, which silently zeroed
  // out this loop for everything except the still-sectionless "Deleted"
  // footer item. `section` is sidebar-grouping presentation, not a hub
  // classifier — don't reintroduce a dependency on it here.
  //
  // `item.visible(persona)` is applied here too (P2.439) — some tenant hub
  // items carry their own capability gate beyond isTenant (Cost needs
  // canViewCost, Deleted needs canManageTenantResources), and the old code
  // only ever re-checked isTenant later, never the hub item's own `visible`.
  for (const item of navigation) {
    if (item.to.startsWith('/tenant/') && item.visible(persona)) {
      const extras = HUB_SEARCH_ALIASES[item.name] ?? []
      cmds.push({
        id: `hub-${item.name}`,
        label: `Go to ${item.name}`,
        value: [item.name, item.to, ...extras].join(' '),
        path: item.to,
      })
    }
  }

  // Hub tab pages — with aliases for old names. Each tab is gated ONLY by its
  // own `visible(persona)`, mirroring HubTabs.tsx:51's
  // `tabs.filter((t) => t.visible(persona))` exactly.
  //
  // Deliberately NOT also gated by the hub's own top-level NavItem.visible:
  // an earlier version of this fix pre-filtered on the hub's NavItem, which
  // assumed a hub's own gate is always a superset of its tabs' gates. That
  // assumption is false for Governance — its NavItem is `p.isTenant` only,
  // but the Compliance/Trust/Log Archive tabs are also reachable by
  // platform_admin (now `roleIn(p, ['tenant_owner', 'tenant_admin',
  // 'platform_admin'])`, mirroring App.tsx's RequireRole on those routes; it
  // previously read `p.isPlatform && p.canViewAudit`, which also admitted
  // platform_auditor — a role those routes reject) — the
  // hub-level pre-check silently hid them from platform_admin, an
  // availability regression (review of P2.439). Every hub tab's own
  // `visible` already encodes the isTenant/isPlatform boundary it needs
  // (verified against navigation.ts), so tab.visible(persona) alone is both
  // necessary and sufficient here.
  for (const hub of hubDefinitions) {
    for (const tab of hub.tabs) {
      if (!tab.visible(persona)) continue
      const terms = [hub.hubName, tab.name, ...(tab.aliases ?? [])].join(' ')
      cmds.push({
        id: `tab-${hub.hubName}-${tab.name}`,
        label: `${hub.hubName} › ${tab.name}`,
        value: terms,
        path: tab.to,
      })
    }
  }

  return cmds
}

// ── Global search — server group presentation ─────────────────────────────────
//
// The backend groups the caller's role can't see are simply ABSENT from the
// response (never assume all thirteen are present). TYPE_ORDER defines a
// stable render order for whichever subset does come back; TYPE_LABELS/
// TYPE_ICON are the palette's presentation of each type — none of this is
// contract from the backend beyond the literal `type` strings below, which
// MUST match internal/api/v1/search/handler.go's fetchGroup(...) calls
// exactly (verified against that file: "workflow", "workflow_engine_ref",
// "execution", "user", "audit_event", "tenant", "agent_definition",
// "agent_skill", "team", "workflow_chain", "runbook",
// "stategraph_definition", "workflow_template" — see the doc comment on
// GlobalSearchResultType in globalSearchApi.ts). A `TYPE_ORDER.map` entry
// whose string doesn't match the backend's literal renders nothing, with no
// error — CommandPalette.test.tsx pins the original six against the
// canonical strings so drift is caught; the seven Build-hub types added
// afterward (agent_definition, agent_skill, team, workflow_chain, runbook,
// stategraph_definition, workflow_template) are covered by a separate test.

const TYPE_ORDER: GlobalSearchResultType[] = [
  'workflow',
  'agent_definition',
  'agent_skill',
  'team',
  'workflow_chain',
  'runbook',
  'stategraph_definition',
  'workflow_template',
  'workflow_engine_ref',
  'execution',
  'user',
  'audit_event',
  'tenant',
]

const TYPE_LABELS: Record<GlobalSearchResultType, string> = {
  workflow: 'Workflows',
  workflow_engine_ref: 'Engine Objects',
  execution: 'Runs',
  user: 'Users',
  audit_event: 'Audit Events',
  tenant: 'Tenants',
  agent_definition: 'Agents',
  agent_skill: 'Skills',
  team: 'Teams',
  workflow_chain: 'Chains',
  runbook: 'Runbooks',
  stategraph_definition: 'Agent Graphs',
  workflow_template: 'Templates',
}

const TYPE_ICON: Record<GlobalSearchResultType, React.ReactNode> = {
  workflow: <Workflow className="h-4 w-4 shrink-0" />,
  workflow_engine_ref: <Bot className="h-4 w-4 shrink-0" />,
  execution: <PlayCircle className="h-4 w-4 shrink-0" />,
  user: <Users className="h-4 w-4 shrink-0" />,
  audit_event: <History className="h-4 w-4 shrink-0" />,
  tenant: <Building2 className="h-4 w-4 shrink-0" />,
  agent_definition: <Bot className="h-4 w-4 shrink-0" />,
  agent_skill: <Wrench className="h-4 w-4 shrink-0" />,
  team: <Network className="h-4 w-4 shrink-0" />,
  workflow_chain: <Link2 className="h-4 w-4 shrink-0" />,
  runbook: <BookOpen className="h-4 w-4 shrink-0" />,
  stategraph_definition: <GitFork className="h-4 w-4 shrink-0" />,
  workflow_template: <LayoutTemplate className="h-4 w-4 shrink-0" />,
}

// ── Agentic shortcuts ─────────────────────────────────────────────────────────
//
// These deep-link to existing pages.  No new API endpoints are created here.
// Targets verified against App.tsx:
//   /tenant/chat                         — ChatPage, line ~346
//   /tenant/monitor?tab=all&status=failed…  — AutomationsPage via the Monitor
//                                              hub route (W11.2)

const AGENTIC_ITEMS = [
  {
    id: 'ask-ai',
    label: 'Ask AI / Open Chat',
    path: '/tenant/ai/chat',
    icon: <MessageSquare className="h-4 w-4 shrink-0" />,
  },
  {
    id: 'diagnose-failed',
    // Honest label — we deep-link to the Runs hub pre-filtered, not an AI endpoint.
    label: 'Show failed runs to diagnose',
    path: '/tenant/monitor?tab=all&status=failed&period=last_24h',
    icon: <AlertCircle className="h-4 w-4 shrink-0" />,
  },
]

// ── Local filtering for sync (non-server) groups ───────────────────────────────
//
// The palette runs the <Command> root with shouldFilter={false}: server
// result groups are already filtered by the backend, and re-running cmdk's
// own fuzzy filter/sort over them would (and did — see the review that
// caught this) silently drop real matches whose value string doesn't happen
// to overlap the typed text (e.g. a workflow that matched on its
// description, or an execution matched by ID against a title that doesn't
// contain it). With shouldFilter=false, cmdk stops filtering/sorting
// *everything*, including the previously-sync groups (Actions, Recent,
// Navigation, Agentic, Context) that relied on cmdk's built-in fuzzy match
// over each item's `value` prop. This helper reimplements that narrowing
// ourselves for those groups only — a plain case-insensitive substring
// match over the same strings that used to be fed to cmdk as `value`. This
// trades away cmdk's fuzzy/typo-tolerant scoring for those groups (e.g. a
// transposed or approximately-spelled query no longer matches) in exchange
// for correctness on the server groups, which is the actual requirement.
function includesQuery(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return haystack.toLowerCase().includes(q)
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CommandPalette() {
  const navigate = useNavigate()
  const persona = usePersona()
  const { open, setOpen } = useCommandPaletteStore()
  const [search, setSearch] = useState('')
  // "Search all history" escalation — scoped to this palette-open session;
  // reset whenever the palette closes (see the reset effect below).
  const [allHistory, setAllHistory] = useState(false)

  // Theme toggle — consumed inside the component to respect rules of hooks.
  const { theme, setTheme } = useTheme()

  // Tenant memberships — for showing "Switch tenant" only when there are > 1.
  const tenantMemberships = useAuthStore((s) => s.tenantMemberships)
  const canSwitchTenant = tenantMemberships.length > 1

  // Recent entity-detail pages from localStorage.
  const recents = useRecentItems()

  // Server-backed global search — debounced/cancelled/sequence-guarded
  // inside the hook. `search` is only ever non-empty while `open` is true
  // (reset below), so this stays idle while the palette is closed.
  const { groups: searchGroups, showSpinner } = useGlobalSearch(search, allHistory)

  const executionGroup = useMemo(
    () => searchGroups.find((g) => g.type === 'execution'),
    [searchGroups]
  )
  const showEscalation =
    !allHistory &&
    search.trim().length >= 2 &&
    (executionGroup?.truncated === true || looksIdLike(search))

  // Reset search + escalation state when palette closes.
  useEffect(() => {
    if (!open) {
      setSearch('')
      setAllHistory(false)
    }
  }, [open])

  const handleSelect = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  // Nav commands are rebuilt per-persona (P2.439) — buildNavCommands now
  // applies each hub item's and hub tab's own `visible(persona)` gate
  // internally, so no further persona filtering is needed here.
  // EXTRA_NAV_COMMANDS (console + favorites pages) are all /tenant/ routes,
  // gated the same way the rest of the file already gates /tenant/ commands.
  // The [persona] dep only actually changes identity when usePersona's own
  // useMemo([role, hasPermission]) recomputes — see usePersona.ts — so this
  // isn't rebuilding on every render.
  const navCommands = useMemo(() => buildNavCommands(persona), [persona])
  const visibleNavCmds = useMemo(
    () => [...navCommands, ...(persona.isTenant ? EXTRA_NAV_COMMANDS : [])],
    [navCommands, persona.isTenant]
  )
  // shouldFilter={false} means cmdk no longer narrows this list itself —
  // reimplement the same substring match over the same `value` string that
  // used to be handed to cmdk (label + aliases + path), so typing an old
  // name ("executions", "airflow") still narrows Navigation.
  const filteredNavCmds = useMemo(
    () => visibleNavCmds.filter((cmd) => includesQuery(cmd.value, search)),
    [visibleNavCmds, search]
  )

  // Gate action commands by persona capability.
  const visibleActions = useMemo(() => ACTION_DEFS.filter((a) => a.gate(persona)), [persona])
  const filteredActions = useMemo(
    () => visibleActions.filter((a) => includesQuery(a.label, search)),
    [visibleActions, search]
  )

  const filteredRecents = useMemo(
    () => recents.filter((item) => includesQuery(`recent ${item.label} ${item.path}`, search)),
    [recents, search]
  )

  const filteredAgenticItems = useMemo(
    () => AGENTIC_ITEMS.filter((item) => includesQuery(item.label, search)),
    [search]
  )

  const showSwitchTenant =
    canSwitchTenant && includesQuery('switch tenant change workspace organisation', search)
  const showToggleTheme = includesQuery(`toggle theme current ${theme}`, search)

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search commands, pages, workflows..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* ── Actions — quick-create shortcuts gated by persona ── */}
        {filteredActions.length > 0 && (
          <>
            <CommandGroup heading="Actions">
              {filteredActions.map((action) => (
                <CommandItem
                  key={action.id}
                  value={action.label}
                  onSelect={() => handleSelect(action.path)}
                >
                  {ACTION_ICONS[action.id]}
                  {action.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* ── Recents — last 5 visited entity-detail pages ── */}
        {filteredRecents.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {filteredRecents.map((item) => (
                <CommandItem
                  key={item.path}
                  value={`recent ${item.label} ${item.path}`}
                  onSelect={() => handleSelect(item.path)}
                >
                  <Clock className="h-4 w-4 shrink-0" />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* ── Navigation — hub entries + tab pages with aliases ── */}
        <CommandGroup heading="Navigation">
          {filteredNavCmds.map((cmd) => (
            <CommandItem
              key={cmd.id}
              // `value` no longer drives cmdk's own filter (shouldFilter=false) —
              // filteredNavCmds above already narrowed by the same string. Kept
              // as the item's identity/data-value.
              value={cmd.value}
              onSelect={() => handleSelect(cmd.path)}
            >
              <Workflow className="h-4 w-4 shrink-0" />
              <HighlightMatch text={cmd.label} query={search} field="label" />
            </CommandItem>
          ))}
        </CommandGroup>

        {/* ── Global search — server groups (replaces the old client-filtered
            Workflows-only fuzzy match). Renders in the same slot the
            Workflows group used to occupy, between Navigation and Agentic. ── */}
        {(showSpinner || searchGroups.some((g) => g.items.length > 0) || showEscalation) && (
          <>
            <CommandSeparator />

            {showSpinner && (
              <div className="text-muted-foreground flex items-center gap-2 px-4 py-2 text-xs">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            )}

            {TYPE_ORDER.map((type) => {
              const group = searchGroups.find((g) => g.type === type)
              if (!group || group.items.length === 0) return null
              return (
                <CommandGroup
                  key={type}
                  heading={
                    <span className="flex items-center gap-1.5">
                      <span className="bg-muted text-muted-foreground inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
                        {TYPE_LABELS[type]}
                      </span>
                      <span>
                        {group.count}
                        {group.truncated ? '+' : ''}
                      </span>
                    </span>
                  }
                >
                  {group.items.map((item) => (
                    // shouldFilter={false}: this item is NEVER locally
                    // filtered — the backend already matched it against the
                    // query (possibly on a field not shown here, e.g. a
                    // description), so re-running any client-side text
                    // match on it would drop real matches. `value` here is
                    // just item identity/data-value, not a filter input.
                    <CommandItem
                      key={item.id}
                      value={[item.title, item.subtitle].filter(Boolean).join(' ')}
                      onSelect={() => handleSelect(item.url)}
                    >
                      {TYPE_ICON[item.type]}
                      <span className="flex min-w-0 flex-col">
                        <HighlightMatch
                          text={item.title}
                          query={search}
                          field="title"
                          highlights={item.highlights}
                          className="truncate"
                        />
                        {item.subtitle && (
                          <HighlightMatch
                            text={item.subtitle}
                            query={search}
                            field="subtitle"
                            highlights={item.highlights}
                            className="text-muted-foreground truncate text-xs"
                          />
                        )}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            })}

            {showEscalation && (
              <CommandGroup heading="More">
                <CommandItem
                  value={`search all history ${search}`}
                  onSelect={() => setAllHistory(true)}
                >
                  <Search className="h-4 w-4 shrink-0" />
                  {`Search all history for "${search}"`}
                </CommandItem>
              </CommandGroup>
            )}
          </>
        )}

        {/* ── Agentic — AI / diagnostic shortcuts ── */}
        {persona.isTenant && filteredAgenticItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Agentic">
              {filteredAgenticItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.label}
                  onSelect={() => handleSelect(item.path)}
                >
                  {item.icon}
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* ── Context — tenant switching + theme toggle ── */}
        <CommandSeparator />
        <CommandGroup heading="Context">
          {showSwitchTenant && (
            <CommandItem
              value="switch tenant change workspace organisation"
              onSelect={() => handleSelect('/select-tenant')}
            >
              <Building2 className="h-4 w-4 shrink-0" />
              Switch tenant...
            </CommandItem>
          )}
          {showToggleTheme && (
            <CommandItem
              value={`toggle theme current ${theme}`}
              onSelect={() => {
                setOpen(false)
                setTheme(theme === 'dark' ? 'light' : 'dark')
              }}
            >
              <SunMoon className="h-4 w-4 shrink-0" />
              Toggle theme
            </CommandItem>
          )}
          {/* B1: "Ask AI" lives ONLY in the Agentic group — the duplicate here
              also silently no-op'd for platform users (Agentic is isTenant-guarded). */}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
